<template>
  <div>
    <!-- App Bar -->
    <nav class="goibibo-header">
      <div class="header-container">
        <!-- Mobile Hamburger Menu -->
        <button class="menu-btn d-md-none" @click="drawer = !drawer">
          <i class="mdi mdi-menu"></i>
        </button>

        <!-- Logo -->
        <div class="header-brand d-flex align-center" @click="$router.push('/')">
          <img src="@/assets/chooseapp-preview.png" alt="ChooseApp Logo" class="logo-img" />
          <span class="brand-text">ChooseApp</span>
        </div>

        <!-- Desktop Navigation -->
        <div class="header-nav d-none d-md-flex">
          <button class="nav-link" :class="{ active: activeTab === 'cabs' }" @click="setActive('cabs', '/')">
            Cabs
          </button>
          <button v-if="isAuthenticated" class="nav-link" :class="{ active: activeTab === 'trips' }" @click="setActive('trips', '/AppTrip')">
            My Trips
          </button>
          <button class="nav-link" :class="{ active: activeTab === 'about' }" @click="scrollToSection('about', 'about')">
            About Us
          </button>
          <button class="nav-link" :class="{ active: activeTab === 'partners' }" @click="scrollToSection('our-partners', 'partners')">
            Our Partners
          </button>
          <button class="nav-link" :class="{ active: activeTab === 'partner-plus' }" @click="scrollToSection('partner', 'partner-plus')">
            Partner+
          </button>
          <button class="nav-link" :class="{ active: activeTab === 'investors' }" @click="scrollToSection('investors', 'investors')">
            Investors
          </button>
          <button class="nav-link" :class="{ active: activeTab === 'press' }" @click="scrollToSection('press', 'press')">
            Press
          </button>
          <button class="nav-link" :class="{ active: activeTab === 'contact' }" @click="scrollToSection('contact-us', 'contact')">
            Contact Us
          </button>
          <button v-if="!isAuthenticated" class="nav-link" :class="{ active: activeTab === 'login' }"
            @click="showLogin = true; activeTab = 'login'">
            Login
          </button>
          <div v-else>
            <v-menu open-on-hover>
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" icon color="#eff6ff" elevation="0">
                  <v-icon color="primary">mdi-account-circle</v-icon>
                </v-btn>
              </template>

              <v-card class="user-dropdown-card" min-width="320" elevation="8">
                <!-- User Profile Header -->
                <div class="user-profile-header">
                  <div class="d-flex align-start pa-4">
                    <v-avatar color="primary" size="56">
                      <v-icon size="32" color="white">mdi-account</v-icon>
                    </v-avatar>
                    <div class="ml-3 flex-grow-1">
                      <div class="text-subtitle-1 font-weight-bold text-left">{{ user.first_name || 'User' }}</div>
                      <div class="text-caption text-medium-emphasis text-left">Account Details</div>
                    </div>
                  </div>
                  
                  <div class="user-details-dropdown">
                    <div class="detail-row">
                      <div class="detail-label">
                        <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-account-outline</v-icon>
                        <span class="text-body-2 text-medium-emphasis">Name</span>
                      </div>
                      <div class="detail-value">{{ user.first_name || 'N/A' }}</div>
                    </div>
                    
                    <div class="detail-row">
                      <div class="detail-label">
                        <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-email-outline</v-icon>
                        <span class="text-body-2 text-medium-emphasis">Email</span>
                      </div>
                      <div class="detail-value">{{ user.email || 'N/A' }}</div>
                    </div>
                    
                    <div class="detail-row">
                      <div class="detail-label">
                        <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-phone-outline</v-icon>
                        <span class="text-body-2 text-medium-emphasis">Phone</span>
                      </div>
                      <div class="detail-value">{{ user.phone || user.mobile || 'N/A' }}</div>
                    </div>
                  </div>
                </div>

                <v-divider></v-divider>

                <!-- Logout Button -->
                <v-list density="compact">
                  <v-list-item @click="logout()" class="logout-item">
                    <template #prepend>
                      <v-icon color="error">mdi-logout</v-icon>
                    </template>
                    <v-list-item-title class="font-weight-medium text-error">
                      Logout
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>
        </div>
      </div>
    </nav>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer v-model="drawer" temporary location="left" app width="320">
      <v-list>
        <!-- Logo in Drawer -->
        <v-list-item class="px-4 py-4">
          <div class="d-flex align-center">
            <img src="@/assets/chooseapp-preview.png" alt="ChooseApp Logo" style="height: 40px; width: auto; margin-right: 8px;" />
            <span class="text-h6 font-weight-bold text-primary">ChooseApp</span>
          </div>
        </v-list-item>

        <v-divider></v-divider>

        <!-- User Profile Section (if logged in) -->
        <div v-if="isAuthenticated && user" class="user-profile-section">
          <div class="d-flex align-start pa-4">
            <v-avatar color="primary" size="56">
              <v-icon size="32" color="white">mdi-account</v-icon>
            </v-avatar>
            <div class="ml-3 flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold text-left">{{ user.first_name || 'User' }}</div>
              <div class="text-caption text-medium-emphasis text-left">Account Details</div>
            </div>
          </div>
          
          <div class="user-details">
            <div class="detail-row">
              <div class="detail-label">
                <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-account-outline</v-icon>
                <span class="text-body-2 text-medium-emphasis">Name</span>
              </div>
              <div class="detail-value">{{ user.first_name || 'N/A' }}</div>
            </div>
            
            <div class="detail-row">
              <div class="detail-label">
                <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-email-outline</v-icon>
                <span class="text-body-2 text-medium-emphasis">Email</span>
              </div>
              <div class="detail-value">{{ user.email || 'N/A' }}</div>
            </div>
            
            <div class="detail-row">
              <div class="detail-label">
                <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-phone-outline</v-icon>
                <span class="text-body-2 text-medium-emphasis">Phone</span>
              </div>
              <div class="detail-value">{{ user.phone || user.mobile || 'N/A' }}</div>
            </div>
          </div>
        </div>

        <v-divider v-if="isAuthenticated"></v-divider>

        <!-- Navigation Items -->
        <v-list-item
          prepend-icon="mdi-taxi"
          @click="setActiveAndClose('cabs', '/')"
          class="my-1 text-start"
        >
          <v-list-item-title>Cabs</v-list-item-title>
        </v-list-item>

        <v-list-item
          v-if="isAuthenticated"
          prepend-icon="mdi-map-marker-path"
          @click="setActiveAndClose('trips', '/AppTrip')"
          class="my-1 text-start"
        >
          <v-list-item-title>My Trips</v-list-item-title>
        </v-list-item>

        <v-list-item
          prepend-icon="mdi-information"
          @click="scrollToSectionAndClose('about', 'about')"
          class="my-1 text-start"
        >
          <v-list-item-title>About Us</v-list-item-title>
        </v-list-item>

        <v-list-item
          prepend-icon="mdi-handshake"
          @click="scrollToSectionAndClose('our-partners', 'partners')"
          class="my-1 text-start"
        >
          <v-list-item-title>Our Partners</v-list-item-title>
        </v-list-item>

        <v-list-item
          prepend-icon="mdi-account-multiple-plus"
          @click="scrollToSectionAndClose('partner', 'partner-plus')"
          class="my-1 text-start"
        >
          <v-list-item-title>Partner+</v-list-item-title>
        </v-list-item>

        <v-list-item
          prepend-icon="mdi-cash-multiple"
          @click="scrollToSectionAndClose('investors', 'investors')"
          class="my-1 text-start"
        >
          <v-list-item-title>Investors</v-list-item-title>
        </v-list-item>

        <v-list-item
          prepend-icon="mdi-newspaper"
          @click="scrollToSectionAndClose('press', 'press')"
          class="my-1 text-start"
        >
          <v-list-item-title>Press</v-list-item-title>
        </v-list-item>

        <v-list-item
          prepend-icon="mdi-email"
          @click="scrollToSectionAndClose('contact-us', 'contact')"
          class="my-1 text-start"
        >
          <v-list-item-title>Contact Us</v-list-item-title>
        </v-list-item>


        <v-divider class="my-2"></v-divider>

        <!-- Login Button (if not logged in) -->
        <v-list-item v-if="!isAuthenticated" @click="handleMobileLogin" class="my-1 text-start">
          <template #prepend>
            <v-icon color="primary">mdi-login</v-icon>
          </template>
          <v-list-item-title class="font-weight-medium text-primary">
            Login
          </v-list-item-title>
        </v-list-item>

        <!-- Logout Button (if logged in) -->
        <v-list-item v-else @click="logout" class="my-1 text-start">
          <template #prepend>
            <v-icon color="error">mdi-logout</v-icon>
          </template>
          <v-list-item-title class="font-weight-medium text-error">
            Logout
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- ✅ Login Popup -->
    <LoginPopup v-if="showLogin" @close="showLogin = false" />
  </div>
</template>

<script>
import LoginPopup from "../components/LoginPopup.vue";
import { mapActions, mapGetters } from 'vuex';

export default {
  name: "AppNavbar",
  components: { LoginPopup },
  data() {
    return {
      drawer: false,
      showLogin: false,
      activeTab: "cabs", // ✅ default to Cabs (homepage)
      isScrolling: false, // Flag to prevent scroll listener during programmatic scroll
    };
  },
  computed: {
    ...mapGetters({
      isAuthenticated: 'auth/authenticated',
      user: 'auth/user'
    })
  },
   watch: {
    // whenever route changes, update activeTab accordingly
    '$route.path'(newPath) {
      if (newPath === '/AppTrip') this.activeTab = 'trips';
      else if (newPath === '/') this.activeTab = 'cabs';
    },
  },
  mounted() {
    console.log('route' , this.$route.path)
    // Add scroll listener to update active tab based on scroll position
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    // Clean up scroll listener
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    ...mapActions({
      logOut: 'auth/logOut'
    }),

    logout() {
      this.logOut();
      this.drawer = false;
      this.$router.push('/');
      this.activeTab = 'cabs';
    },
    setActive(tab, route) {
      this.activeTab = tab;
      if (tab === 'cabs') {
        // Disable scroll listener during programmatic scroll
        this.isScrolling = true;
        
        // If already on home page, just scroll to top
        if (this.$route.path === '/') {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } else {
          // Navigate to home page first, then scroll to top
          this.$router.push(route).then(() => {
            this.$nextTick(() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            });
          });
        }
        
        // Re-enable scroll listener after animation completes
        setTimeout(() => {
          this.isScrolling = false;
        }, 1000);
      } else {
        this.$router.push(route);
      }
    },
    setActiveAndClose(tab, route) {
      this.activeTab = tab;
      this.drawer = false;
      if (tab === 'cabs') {
        // If already on home page, just scroll to top
        if (this.$route.path === '/') {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } else {
          // Navigate to home page first, then scroll to top
          this.$router.push(route).then(() => {
            this.$nextTick(() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            });
          });
        }
      } else {
        this.$router.push(route);
      }
    },
    handleMobileLogin() {
      this.showLogin = true;
      this.activeTab = 'login';
      this.drawer = false;
    },
    openFilters() {
      this.$emit("open-filters");
      this.drawer = false;
    },
    scrollToSection(sectionId, tabName) {
      // Set active tab
      this.activeTab = tabName;
      
      // Disable scroll listener during programmatic scroll
      this.isScrolling = true;
      
      // Navigate to home page first if not already there
      if (this.$route.path !== '/') {
        this.$router.push('/').then(() => {
          this.$nextTick(() => {
            this.performScroll(sectionId);
          });
        });
      } else {
        this.performScroll(sectionId);
      }
      
      // Re-enable scroll listener after animation completes
      setTimeout(() => {
        this.isScrolling = false;
      }, 1000);
    },
    scrollToSectionAndClose(sectionId, tabName) {
      this.drawer = false;
      this.scrollToSection(sectionId, tabName);
    },
    performScroll(sectionId) {
      // For all sections, find the element and scroll to it
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          // For "about" section, use a larger offset to account for hero section
          const headerOffset = sectionId === 'about' ? 400 : 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    },
    handleScroll() {
      // Only track scroll on home page
      if (this.$route.path !== '/') return;
      
      // Don't update active tab during programmatic scrolling
      if (this.isScrolling) return;

      const sections = [
        { id: 'contact-us', tab: 'contact' },
        { id: 'press', tab: 'press' },
        { id: 'investors', tab: 'investors' },
        { id: 'partner', tab: 'partner-plus' },
        { id: 'our-partners', tab: 'partners' },
        { id: 'about', tab: 'about' }
      ];

      const scrollPosition = window.scrollY + 150; // Offset for navbar height

      // Check if we're at the very top (within first 200px)
      if (window.scrollY < 200) {
        this.activeTab = 'cabs';
        return;
      }

      // Check each section from bottom to top
      for (let i = 0; i < sections.length; i++) {
        const element = document.getElementById(sections[i].id);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;

          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            this.activeTab = sections[i].tab;
            return;
          }
        }
      }
    },
  },
};
</script>

<style scoped>
.goibibo-header {
  background: #fff;
  border-bottom: 1px solid #ddd;
  padding: 8px 16px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  box-sizing: border-box;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.logo-img {
  height: 50px;
  width: auto;
  margin-right: 8px;
}

.brand-text {
  font-size: 18px;
  font-weight: 600;
  color: #005886;
  white-space: nowrap;
}

.header-brand {
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .brand-text {
    font-size: 16px;
  }
  
  .logo-img {
    height: 40px;
  }
  
  .goibibo-header {
    padding: 8px 12px;
  }
}

.header-nav {
  display: flex;
  align-items: center;
}

.header-nav .nav-link {
  background: none;
  border: none;
  margin-left: 12px;
  font-size: 15px;
  cursor: pointer;
  color: #333;
  padding: 8px 4px;
  position: relative;
}

.header-nav .nav-link.active {
  color: #2e69b3;
  font-weight: 600;
}

.header-nav .nav-link.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  height: 3px;
  width: 100%;
  background-color: #2e69b3;
  border-radius: 2px;
}

.menu-btn {
  border: none;
  background: none;
  font-size: 26px;
  cursor: pointer;
  color: #333;
  padding: 4px;
  margin-right: 8px;
}

.menu-btn:hover {
  color: #2e69b3;
}

/* User Profile Section in Drawer */
.user-profile-section {
  background-color: #f8f9fa;
}

.user-details {
  background-color: #ffffff;
  border-radius: 8px;
  margin: 0 16px 16px 16px;
  padding: 16px;
}

.detail-row {
  margin-bottom: 16px;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  text-align: left;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  margin-left: 26px;
  text-align: left;
  word-break: break-word;
}

/* Desktop User Dropdown Styles */
.user-dropdown-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.user-profile-header {
  background-color: #f8f9fa;
}

.user-details-dropdown {
  background-color: #ffffff;
  border-radius: 8px;
  margin: 0 16px 16px 16px;
  padding: 16px;
}

.user-details-dropdown .detail-row {
  margin-bottom: 16px;
}

.user-details-dropdown .detail-row:last-child {
  margin-bottom: 0;
}

.user-details-dropdown .detail-label {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  text-align: left;
}

.user-details-dropdown .detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  margin-left: 26px;
  text-align: left;
  word-break: break-word;
}

.logout-item {
  padding: 12px 16px !important;
}

.logout-item:hover {
  background-color: #ffebee !important;
}
</style>
